import { API } from '@xbto/api-client';
import { action, Action, Computed, computed, thunk } from 'easy-peasy';
import { getYear } from 'date-fns';
import {
  runApi,
  factories,
  dateRangesUTC,
  DateRangeUTCId,
  ApiResponseWithPagination,
  startOfYearUTC,
  endOfYearUTC,
} from '../../utils';
import { ApiThunk, AppThunk } from '../types';
import {
  EnrichedAccountStatement,
  EnrichedGroupedAccountStatement,
} from '../../types/enriched';
import { DataModel } from '../data-store';
import { BaseModel, createBaseModel } from '../base-store';
import { ApiError } from '../api-error';
import { ERROR_MESSAGES } from '../../constants';

const accountTypeGroups: Record<
  API.AccountType.ProTrading | API.AccountType.Fund | API.AccountType.Custody,
  API.AccountType[]
> = {
  [API.AccountType.ProTrading]: [
    API.AccountType.Interest,
    API.AccountType.Pool,
    API.AccountType.ProTrading,
    API.AccountType.Trading,
  ],
  [API.AccountType.Fund]: [API.AccountType.Fund],
  [API.AccountType.Custody]: [API.AccountType.Custody, API.AccountType.Vault],
};

type StatementsThunk<Payload, Result> = AppThunk<
  StatementsModel,
  Payload,
  Result
>;

export type StatementsFilters = Omit<
  API.ListAccountStatementRequest,
  'limit' | 'page'
> & {
  dateRangeId: DateRangeUTCId | null;
  limit: number;
  page: number;
};

export type AccountTypeOption = { label: string; value: API.AccountType };
export type DateRangeOption = {
  label: string;
  id: DateRangeUTCId;
};

const initialFilters: StatementsFilters = {
  accountId: null,
  accountTypes: null,
  assetCode: null,
  dateRangeId: null,
  endDate: null,
  limit: 20,
  page: 1,
  startDate: null,
};

const accountTypes = [
  API.AccountType.ProTrading,
  API.AccountType.Fund,
  API.AccountType.Custody,
];

export const accountTypeNames: Record<API.AccountType, string> = {
  [API.AccountType.ProTrading]: 'Trading & Cash',
  [API.AccountType.Fund]: 'Funds',
  [API.AccountType.Custody]: 'Custody',
  [API.AccountType.Interest]: 'Trading & Cash',
  [API.AccountType.Trading]: 'Trading & Cash',
  [API.AccountType.Pool]: 'Trading & Cash',
  [API.AccountType.Vault]: 'Custody',
};

function getFiltersCount(req: API.ListAccountStatementRequest): number {
  let count = 0;

  if (req.accountTypes) {
    count += req.accountTypes.length;
  }

  if (req.startDate && req.endDate) {
    count += 1;
  }

  return count;
}

const initialDateRangeOptions: DateRangeOption[] = dateRangesUTC.map(
  ({ id, label }) => {
    return { label, id };
  }
);

const accountTypeOptions: AccountTypeOption[] = accountTypes.map(
  accountType => {
    return {
      label: accountTypeNames[accountType],
      value: accountType,
    };
  }
);

export interface StatementsModel extends BaseModel {
  // statements
  _list: API.GroupedAccountStatement[] | null;
  setList: Action<StatementsModel, API.GroupedAccountStatement[] | null>;
  getList: ApiThunk<
    StatementsModel,
    API.ListAccountStatementRequest,
    API.GroupedAccountStatement[]
  >;
  list: Computed<
    StatementsModel,
    EnrichedGroupedAccountStatement[] | null,
    DataModel
  >;
  listFull: EnrichedAccountStatement[] | null;
  _firstStatementDate: Date | null;
  setListFull: Action<StatementsModel, API.AccountStatementResponse>;
  appendToListFull: Action<StatementsModel, API.AccountStatementResponse>;
  getListFull: ApiThunk<
    StatementsModel,
    API.ListAccountStatementRequest,
    API.AccountStatementResponse
  >;
  paginateListFull: StatementsThunk<void, Promise<void>>;
  refreshListFull: StatementsThunk<void, void>;
  hasNoStatementsYet: Computed<StatementsModel, boolean>;
  isLastPage: boolean;
  _setIsLastPage: Action<StatementsModel, ApiResponseWithPagination<unknown>>;

  // statement
  getStatement: ApiThunk<
    StatementsModel,
    {
      statementId: string;
      accountId: string;
    },
    API.GetStatementPdfResponse
  >;
  getStatementPdfUrl: StatementsThunk<
    { statementId: string; accountId: string },
    Promise<string>
  >;
  getLatestStatementPdf: ApiThunk<
    StatementsModel,
    { accountId: string; assetCode: string },
    {
      statement: EnrichedAccountStatement;
      pdf: API.GetStatementPdfResponse | null;
    }
  >;

  // filters
  filters: StatementsFilters;
  appliedFiltersCount: number;
  setAppliedFiltersCount: Action<StatementsModel, number>;
  filtersCount: Computed<StatementsModel, number>;
  filterAccountType: Computed<StatementsModel, API.AccountType | null>;
  dateRangeOptions: Computed<StatementsModel, DateRangeOption[]>;
  accountTypeOptions: AccountTypeOption[];
  setFilters: Action<StatementsModel, StatementsFilters | null>;
  applyFilters: StatementsThunk<void, void>;
  clearFilters: StatementsThunk<void, void>;
  setFilterAccountType: Action<StatementsModel, API.AccountType | null>;
  setFilterDateRange: StatementsThunk<DateRangeUTCId, void>;
}

export const statementsModel: StatementsModel = {
  ...createBaseModel(),

  // statements
  _list: null,
  setList: action((state, payload) => {
    state._list = payload;
  }),
  getList: thunk((actions, payload, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.accountStatementList(
          Object.assign(
            {
              startDate: null,
              endDate: null,
              accountId: helpers.getStoreState().user.defaultAccountId,
            },
            payload,
            {
              // Force no pagination
              limit: null,
              page: null,
            }
          )
        );
      },
      result => {
        actions.setList(result);
      },
      {
        'x-account-id':
          payload.accountId || helpers.getStoreState().user.defaultAccountId,
      }
    );
  }),
  listFull: null,
  _firstStatementDate: null,
  setListFull: action((state, payload) => {
    const enrichedStatements = (payload.statements ?? []).map(
      factories.enrichAccountStatement
    );

    state.listFull = enrichedStatements;

    if (!state._firstStatementDate) {
      // We set once at page load
      state._firstStatementDate = payload.firstStatementDate;
    }
  }),
  appendToListFull: action((state, payload) => {
    const oldStatements = state.listFull ?? [];
    const newStatements = (payload.statements ?? []).map(
      factories.enrichAccountStatement
    );

    state.listFull = [...oldStatements, ...newStatements];
  }),
  getListFull: thunk((actions, req, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        actions.setAppliedFiltersCount(getFiltersCount(req));

        const accountType = req.accountTypes?.[0] ?? null;

        const _req = accountType
          ? {
              ...req,
              accountTypes: accountTypeGroups[accountType] ?? [accountType],
            }
          : req;

        return helpers.injections.apiClient.accountStatementListFull(_req);
      },
      result => {
        if (req.page === 1) {
          actions.setListFull(result);
        } else {
          actions.appendToListFull(result);
        }
      },
      {
        'x-account-id':
          req.accountId || helpers.getStoreState().user.defaultAccountId,
      },
      {
        onError: errorMessage => {
          actions.setError(errorMessage);
        },
        onResponse: res => {
          actions._setIsLastPage(res);
        },
      }
    );
  }),
  paginateListFull: thunk(async (actions, _void, { getState }) => {
    const { dateRangeId, ...req } = getState().filters;

    req.page = req.page + 1;

    // Save new filters
    actions.setFilters({ ...req, dateRangeId });

    // Refresh list
    await actions.getListFull(req);
  }),
  refreshListFull: thunk((actions, _void, { getState }) => {
    const { dateRangeId: _unused, ...req } = getState().filters;

    actions.setError(null);
    actions.getListFull(req);
  }),
  list: computed([s => s._list], _list => {
    if (!_list) {
      return null;
    }

    return _list.map(factories.enrichGroupedAccountStatement);
  }),
  hasNoStatementsYet: computed(
    [s => s.listFull, s => s._firstStatementDate],
    (listFull, _firstStatementDate) => {
      return listFull !== null && _firstStatementDate === null;
    }
  ),
  isLastPage: false,
  _setIsLastPage: action((state, payload) => {
    state.isLastPage =
      typeof payload.page !== 'undefined' &&
      typeof payload.totalPages !== 'undefined'
        ? payload.page >= payload.totalPages
        : false;
  }),

  // statement
  getStatement: thunk((actions, { accountId, statementId }, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        return helpers.injections.apiClient.getStatementPdf(statementId);
      },
      undefined,
      {
        'x-account-id': accountId,
      }
    );
  }),
  getStatementPdfUrl: thunk(async (actions, { accountId, statementId }) => {
    const { isSuccessful, result, errorMessage } = await actions.getStatement({
      accountId,
      statementId,
    });

    if (isSuccessful && result?.url) {
      return result.url;
    }

    throw new Error(errorMessage ?? ERROR_MESSAGES.DEFAULT);
  }),
  getLatestStatementPdf: thunk((actions, { accountId, assetCode }, helpers) => {
    return runApi(
      actions,
      helpers,
      async () => {
        const statementResponse =
          await helpers.injections.apiClient.accountStatementListFull({
            limit: 1,
            startDate: null,
            endDate: null,
            accountId,
            assetCode,
            page: null,
            accountTypes: null,
          });

        const list = statementResponse.result?.statements ?? [];
        const statement = (list[0] ?? null) as API.AccountStatement | null;
        if (statement?.accountStatementId) {
          const pdfResponse =
            await helpers.injections.apiClient.getStatementPdf(
              statement.accountStatementId
            );
          return {
            ...pdfResponse,
            result: {
              statement: factories.enrichAccountStatement(statement),
              pdf: pdfResponse.result,
            },
          };
        } else {
          throw new ApiError(
            404,
            ERROR_MESSAGES.STATEMENT_DOWNLOAD_MISSING,
            -1
          );
        }
      },
      undefined,
      {
        'x-account-id': accountId,
      }
    );
  }),

  // filters
  filters: initialFilters,
  filterAccountType: computed([s => s.filters], filters => {
    return filters.accountTypes?.[0] ?? null;
  }),
  appliedFiltersCount: 0,
  setAppliedFiltersCount: action((state, payload) => {
    state.appliedFiltersCount = payload;
  }),
  filtersCount: computed([s => s.filters], filters => {
    return getFiltersCount(filters);
  }),
  dateRangeOptions: computed(
    [s => s._firstStatementDate],
    _firstStatementDate => {
      if (!_firstStatementDate) {
        return initialDateRangeOptions;
      }

      const options = [...initialDateRangeOptions];

      const currentYear = getYear(new Date());
      const firstYear = getYear(new Date(_firstStatementDate));

      const failSafeYear = 2020;
      const minYear = Math.max(firstYear, failSafeYear);

      for (let year = currentYear - 1; year >= minYear; year--) {
        options.push({
          label: `${year}`,
          id: year,
        });
      }

      return options;
    }
  ),
  accountTypeOptions,
  setFilters: action((state, payload) => {
    state.filters = payload ?? initialFilters;
  }),
  clearFilters: thunk(actions => {
    actions.setFilters(null);
  }),
  applyFilters: thunk((actions, _void, { getState }) => {
    const { dateRangeId, ...req } = getState().filters;

    // Reset page to 1 when applying filters
    req.page = 1;

    // Save new filters
    actions.setFilters({ ...req, dateRangeId });

    // Refresh list
    actions.getListFull(req);
  }),
  setFilterAccountType: action((state, newAccountType) => {
    state.filters = {
      ...state.filters,
      accountTypes: newAccountType ? [newAccountType] : null,
    };
  }),
  setFilterDateRange: thunk((actions, newDateRangeId, { getState }) => {
    let endDate: Date | null = null;
    let startDate: Date | null = null;

    const oldState = getState();
    const oldFilters = oldState.filters;
    const newFilters = { ...oldFilters };

    if (newDateRangeId === oldFilters.dateRangeId) {
      // Toggle off
      newFilters.dateRangeId = null;

      startDate = null;
      endDate = null;
    } else if (typeof newDateRangeId === 'number') {
      newFilters.dateRangeId = newDateRangeId;

      const year = new Date(newDateRangeId, 0, 1);

      startDate = startOfYearUTC(year);
      endDate = endOfYearUTC(year);
    } else {
      const dateRange =
        dateRangesUTC.find(({ id }) => id === newDateRangeId) ?? null;

      if (dateRange) {
        newFilters.dateRangeId = newDateRangeId;

        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      }
    }

    newFilters.startDate = startDate;
    newFilters.endDate = endDate;

    actions.setFilters(newFilters);
  }),
};
